/**
 * Platform abstraction interface for cross-platform code generation
 */

import type { ParsedModule } from "../parser";
import type { ScannedModule } from "../scanner";

/**
 * Base interface with common methods for all platforms
 */
interface BasePlatformGenerator {
  /**
   * Check if this generator supports the target platform
   */
  supports(target: string): boolean;

  /**
   * Generate bridge code for the platform
   * @param modules Parsed module specifications
   * @param scannedModules All scanned brick modules from dependencies
   */
  generateBridge(
    modules: ParsedModule[],
    scannedModules?: ScannedModule[],
  ): Promise<string[]>;

  /**
   * Check if platform is available in current environment
   */
  isAvailable(): boolean;
}

/**
 * iOS-specific platform generator interface
 */
export interface IOSPlatformGeneratorInterface extends BasePlatformGenerator {
  /**
   * Platform name identifier - always "ios" for iOS platform
   */
  getPlatformName(): "ios";

  /**
   * Generate library types for iOS (Swift structs)
   */
  generateLibraryTypes(module: ParsedModule): Promise<string>;

  /**
   * Generate library protocol for iOS (Swift protocol)
   */
  generateLibraryProtocol(module: ParsedModule): Promise<string>;
}

/**
 * Android-specific platform generator interface
 */
export interface AndroidPlatformGeneratorInterface
  extends BasePlatformGenerator {
  /**
   * Platform name identifier - always "android" for Android platform
   */
  getPlatformName(): "android";

  /**
   * Generate library types for Android (Kotlin data classes)
   */
  generateLibraryTypes(module: ParsedModule): Promise<string>;

  /**
   * Generate library interface for Android (Kotlin interface)
   */
  generateLibraryInterface(module: ParsedModule): Promise<string>;
}

/**
 * Discriminated union type for platform generators
 */
export type PlatformGenerator =
  | IOSPlatformGeneratorInterface
  | AndroidPlatformGeneratorInterface;

/**
 * Type guard to check if a generator is for iOS
 */
export function isIOSPlatformGenerator(
  generator: PlatformGenerator,
): generator is IOSPlatformGeneratorInterface {
  return generator.getPlatformName() === "ios";
}

/**
 * Type guard to check if a generator is for Android
 */
export function isAndroidPlatformGenerator(
  generator: PlatformGenerator,
): generator is AndroidPlatformGeneratorInterface {
  return generator.getPlatformName() === "android";
}

export type SupportedPlatform = "ios" | "android";
